import { useEffect, useMemo, useState, type FormEvent } from "react";
import type { AdminAlertItem, AdminAlertPayload } from "../api/adminAlertsApi";
import { createAdminAlert, deleteAdminAlert, getAdminAlertById, getAdminAlerts, updateAdminAlert } from "../api/adminAlertsApi";
import { useToast } from "../context/ToastContext";
import InlineAlert from "../Components/ui/InlineAlert";
import InfoMarkerHint from "../Components/ui/InfoMarkerHint";
import { confirmDialog } from "../utils/dialogs";

const audienceOptions = [
  { value: "all", label: "Tüm kullanıcılar" },
  { value: "authenticated", label: "Giriş yapan kullanıcılar" },
  { value: "guest", label: "Misafir kullanıcılar" },
  { value: "creator", label: "Creator rolündeki kullanıcılar" },
  { value: "moderator", label: "Moderatörler" },
  { value: "admin", label: "Admin" },
] as const;

const toneOptions: Array<{ value: AdminAlertItem["tone"]; label: string }> = [
  { value: "info", label: "Bilgilendirme" },
  { value: "success", label: "Başarılı" },
  { value: "warning", label: "Uyarı" },
  { value: "danger", label: "Kritik" },
];

const toneCardStyle: Record<AdminAlertItem["tone"], string> = {
  info: "border-sky-200 bg-sky-50 text-sky-900",
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  danger: "border-rose-200 bg-rose-50 text-rose-900",
};

const defaultForm: AdminAlertPayload = {
  title: "",
  message: "",
  tone: "info",
  audience: "all",
  targetUrl: "",
  isActive: true,
  isDismissible: false,
  showOnHome: true,
  showOnReader: false,
  showOnCreator: false,
  showOnGlobal: false,
  startAtUtc: new Date().toISOString().slice(0, 16),
  endAtUtc: "",
  priority: 0,
};

function normalizeMojibake(value: string): string {
  if (!value || !/[\u00C3\u00C2\u00C4\u00C5\u00E2]/.test(value)) {
    return value;
  }

  try {
    const bytes = Uint8Array.from(Array.from(value).map((char) => char.charCodeAt(0) & 0xff));
    const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return decoded.includes("\uFFFD") ? value : decoded;
  } catch {
    return value;
  }
}

export default function AdminAlerts() {
  const toast = useToast();
  const [alerts, setAlerts] = useState<AdminAlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAdminAlerts();
        setAlerts(data);
      } catch (err) {
        console.error(err);
        setError("Alert listesi yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const orderedAlerts = useMemo(
    () => [...alerts].sort((a, b) => new Date(b.startAtUtc).getTime() - new Date(a.startAtUtc).getTime()),
    [alerts],
  );

  const applyFormFromAlert = (alert: AdminAlertItem) => {
    setForm({
      title: normalizeMojibake(alert.title),
      message: normalizeMojibake(alert.message),
      tone: alert.tone,
      audience: alert.audience,
      targetUrl: alert.targetUrl ?? "",
      isActive: alert.isActive,
      isDismissible: alert.isDismissible,
      showOnHome: alert.showOnHome,
      showOnReader: alert.showOnReader,
      showOnCreator: alert.showOnCreator,
      showOnGlobal: alert.showOnGlobal,
      startAtUtc: toInputDate(alert.startAtUtc),
      endAtUtc:alert.endAtUtc ? toInputDate(alert.endAtUtc) : "",
      priority: alert.priority ?? 0,
    });
  };

  const handleSelect = async (alert: AdminAlertItem) => {
    setSelectedId(alert.id);
    applyFormFromAlert(alert);

    const fromApi = await getAdminAlertById(alert.id);
    if (fromApi) {
      applyFormFromAlert(fromApi);
    }
  };

  const resetForm = () => {
    setSelectedId(null);
    setForm({
      ...defaultForm,
      startAtUtc: new Date().toISOString().slice(0, 16),
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const payload: AdminAlertPayload = {
      ...form,
      title: form.title.trim(),
      message: form.message.trim(),
      targetUrl: form.targetUrl?.trim() || "",
      startAtUtc: new Date(form.startAtUtc).toISOString(),
      endAtUtc:form.endAtUtc ? new Date(form.endAtUtc).toISOString() : undefined,
    };

    if (!payload.title || !payload.message) {
      toast.error("Başlık ve mesaj zorunludur.");
      return;
    }

    try {
      if (selectedId) {
        const updated = await updateAdminAlert(selectedId, payload);
        if (!updated) {
          throw new Error("update-failed");
        }
        toast.success("Alert güncellendi.");
        setAlerts((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await createAdminAlert(payload);
        if (!created) {
          throw new Error("create-failed");
        }
        toast.success("Yeni alert oluşturuldu.");
        setAlerts((prev) => [created, ...prev]);
        setSelectedId(created.id);
      }
    } catch (err) {
      console.error(err);
      toast.error("Kayıt sırasında hata oluştu.");
      setError("Değişiklik kaydedilemedi.");
    }
  };

  const handleDelete = async () => {
    if (!selectedId) {
      return;
    }

    const confirmed = await confirmDialog({
      title: "Alert kaydını sil",
      text: "Bu alert kaydını silmek istediğine emin misin?",
      confirmText: "Evet, sil",
      danger: true,
    });
    if (!confirmed) {
      return;
    }

    try {
      const success = await deleteAdminAlert(selectedId);
      if (!success) {
        throw new Error("delete-failed");
      }
      toast.success("Alert silindi.");
      setAlerts((prev) => prev.filter((item) => item.id !== selectedId));
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error("Alert silinemedi.");
    }
  };

  return (
    <div className="workspace-canvas space-y-5">
      <section className="workspace-panel rounded-3xl p-6">
        <div className="inline-flex items-center gap-2">
          <h2 className="text-xl font-bold text-slate-900">Global Alert Yönetimi</h2>
          <InfoMarkerHint markerKey="alerts.system" />
        </div>
        <p className="text-sm text-slate-600">Anasayfa, reader ve creator alanlarındaki duyuruları tek merkezden yönet.</p>
      </section>

      {error && <InlineAlert tone="warning">{error}</InlineAlert>}

      <div className="grid gap-5 xl:grid-cols-[380px,1fr]">
        <section className="workspace-panel rounded-3xl p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-800">Alert Listesi</h3>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Yeni alert
            </button>
          </div>

          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">Alert listesi yükleniyor...</div>
          ) : orderedAlerts.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">Henüz alert bulunmuyor.</div>
          ) : (
            <ul className="space-y-2">
              {orderedAlerts.map((alert) => (
                <li
                  key={alert.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-slate-300"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">{normalizeMojibake(alert.title)}</div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {new Date(alert.startAtUtc).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })} •{" "}
                        {alert.isActive ? "Aktif" : "Pasif"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleSelect(alert)}
                      className="rounded border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      Düzenle
                    </button>
                  </div>

                  <div className="mt-2 line-clamp-2 text-[11px] leading-5 text-slate-600">{normalizeMojibake(alert.message)}</div>

                  <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px]">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5">{renderAudienceLabel(alert.audience)}</span>
                    <span className={`rounded-full border px-2 py-0.5 ${toneCardStyle[alert.tone]}`}>{renderToneLabel(alert.tone)}</span>
                    {!!alert.priority && (
                      <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-violet-800">Öncelik {alert.priority}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="workspace-panel rounded-3xl p-6">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">
                Başlık
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  required
                />
              </label>

              <label className="text-sm font-medium text-slate-700">
                Ton
                <select
                  value={form.tone}
                  onChange={(e) => setForm((prev) => ({ ...prev, tone: e.target.value as AdminAlertItem["tone"] }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                >
                  {toneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="text-sm font-medium text-slate-700">
              Mesaj
              <textarea
                value={form.message}
                onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                rows={4}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                required
              />
            </label>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">
                Hedef kitle
                <select
                  value={form.audience}
                  onChange={(e) => setForm((prev) => ({ ...prev, audience: e.target.value as AdminAlertItem["audience"] }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                >
                  {audienceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm font-medium text-slate-700">
                Öncelik
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm((prev) => ({ ...prev, priority: Number(e.target.value) }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
            </div>

            <label className="text-sm font-medium text-slate-700">
              Hedef URL (opsiyonel)
              <input
                type="text"
                value={form.targetUrl ?? ""}
                onChange={(e) => setForm((prev) => ({ ...prev, targetUrl: e.target.value }))}
                placeholder="/content/..."
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
            </label>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">
                Başlangıç
                <input
                  type="datetime-local"
                  value={form.startAtUtc}
                  onChange={(e) => setForm((prev) => ({ ...prev, startAtUtc: e.target.value }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  required
                />
              </label>

              <label className="text-sm font-medium text-slate-700">
                Bitiş (opsiyonel)
                <input
                  type="datetime-local"
                  value={form.endAtUtc ?? ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, endAtUtc: e.target.value }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
            </div>

            <div className="grid gap-2 text-sm md:grid-cols-2">
              <label className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                Aktif
              </label>

              <label className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  checked={form.isDismissible}
                  onChange={(e) => setForm((prev) => ({ ...prev, isDismissible: e.target.checked }))}
                />
                Kullanıcı kapatabilir
              </label>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs text-slate-700 md:grid-cols-4">
              <label className="flex items-center gap-2 rounded border border-slate-200 bg-white/70 p-2">
                <input
                  type="checkbox"
                  checked={form.showOnGlobal}
                  onChange={(e) => setForm((prev) => ({ ...prev, showOnGlobal: e.target.checked }))}
                />
                Global
              </label>
              <label className="flex items-center gap-2 rounded border border-slate-200 bg-white/70 p-2">
                <input
                  type="checkbox"
                  checked={form.showOnHome}
                  onChange={(e) => setForm((prev) => ({ ...prev, showOnHome: e.target.checked }))}
                />
                Ana sayfa
              </label>
              <label className="flex items-center gap-2 rounded border border-slate-200 bg-white/70 p-2">
                <input
                  type="checkbox"
                  checked={form.showOnReader}
                  onChange={(e) => setForm((prev) => ({ ...prev, showOnReader: e.target.checked }))}
                />
                Reader
              </label>
              <label className="flex items-center gap-2 rounded border border-slate-200 bg-white/70 p-2">
                <input
                  type="checkbox"
                  checked={form.showOnCreator}
                  onChange={(e) => setForm((prev) => ({ ...prev, showOnCreator: e.target.checked }))}
                />
                Creator
              </label>
            </div>

            <section className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Önizleme</p>
              <div className={`mt-2 rounded-xl border px-3 py-2 text-sm ${toneCardStyle[form.tone]}`}>
                <div className="font-semibold">{form.title || "Alert başlığı"}</div>
                <div className="mt-1 text-xs leading-relaxed opacity-90">{form.message || "Alert mesajı"}</div>
              </div>
            </section>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="submit"
                className="rounded-xl bg-[linear-gradient(135deg,#0a84ff,#0a5dd7)] px-5 py-2 text-sm font-semibold text-white"
              >
                {selectedId ? "Alert güncelle" : "Yeni alert oluştur"}
              </button>

              {selectedId && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="rounded-xl border border-rose-300 bg-rose-50 px-5 py-2 text-sm font-semibold text-rose-700"
                >
                  Sil
                </button>
              )}

              <button
                type="button"
                onClick={resetForm}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                Temizle
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

function toInputDate(value: string) {
  const date = new Date(value);
  const iso = date.toISOString();
  return iso.slice(0, 16);
}

function renderAudienceLabel(value: AdminAlertItem["audience"]) {
  const map: Record<AdminAlertItem["audience"], string> = {
    all: "Tüm kullanıcılar",
    authenticated: "Giriş yapanlar",
    guest: "Misafirler",
    creator: "Creator",
    moderator: "Moderatör",
    admin: "Admin",
  };
  return map[value] ?? value;
}

function renderToneLabel(value: AdminAlertItem["tone"]) {
  const map: Record<AdminAlertItem["tone"], string> = {
    info: "Bilgilendirme",
    success: "Başarı",
    warning: "Uyarı",
    danger: "Kritik",
  };
  return map[value] ?? value;
}
